import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, styled, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { Notification, NotificationType } from './types';
import { RootState } from '../../store';
import { removeNotification } from './slices/notificationSlice';
import { colors } from '../theme/settings';

export const StyledNotificationComponent = (bgColor: string, textColor: string, sx?: any) =>
    styled('div')({
        padding: '8px',
        backgroundColor: bgColor,
        color: textColor,
        display: 'flex',
        justifyContent: 'space-between',
        borderRadius: '4px',
        marginBottom: '8px',
        position: 'relative',
        '@media (max-width: 600px)': {
            maxWidth: '320px',
            minWidth: '320px',
        },
        fontSize: '14px',
        ...sx,
    });

const notificationComponents = {
    SUCCESS: StyledNotificationComponent(colors.system.success.bg, colors.system.success.text),
    ERROR: StyledNotificationComponent(colors.system.error.bg, colors.system.error.text),
    WARNING: StyledNotificationComponent(colors.system.warn.bg, colors.system.warn.text),
    INFO: StyledNotificationComponent(colors.system.info.bg, colors.system.info.text),
};

const NotificationContainer = styled(Box)({
    position: 'fixed',
    top: '50px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    width: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: '14px',
});

const GlobalNotification = () => {
    const dispatch = useDispatch();
    const notificationsList = useSelector((state: RootState) => state.notifications.notifications);

    useEffect(() => {
        const timers = notificationsList.map((notification: Notification) =>
            setTimeout(() => {
                dispatch(removeNotification(notification.timestamp));
            }, 3000)
        );

        return () => {
            timers.forEach(clearTimeout);
        };
    }, [notificationsList, dispatch]);

    const getIcon = (type: NotificationType) => {
        switch (type) {
            case NotificationType.SUCCESS:
                return (
                    <CheckCircleOutlinedIcon
                        data-testid='success-icon'
                        sx={{ marginRight: '8px', color: colors.system.success.main }}
                    />
                );
            case NotificationType.ERROR:
                return (
                    <InfoOutlinedIcon
                        data-testid='error-icon'
                        sx={{ marginRight: '8px', color: colors.system.error.main }}
                    />
                );
            case NotificationType.WARNING:
                return (
                    <InfoOutlinedIcon
                        data-testid='warn-icon'
                        sx={{ marginRight: '8px', color: colors.system.warn.main }}
                    />
                );
            case NotificationType.INFO:
                return (
                    <InfoOutlinedIcon
                        data-testid='info-icon'
                        sx={{ marginRight: '8px', color: colors.system.info.main }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <NotificationContainer id='notification-container'>
            {notificationsList.map((notification: Notification) => {
                const NotificationComponent = notificationComponents[notification.type];
                return (
                    <NotificationComponent key={notification.timestamp}>
                        {getIcon(notification.type)}
                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>{notification.message}</Box>
                        <IconButton
                            data-testid={`close-button-${notification.timestamp}`}
                            size='small'
                            aria-label='close'
                            color='inherit'
                            onClick={() => dispatch(removeNotification(notification.timestamp))}
                        >
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </NotificationComponent>
                );
            })}
        </NotificationContainer>
    );
};

export default GlobalNotification;
